import { Button, CircularProgress } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import "./AlertDialog.scss";

export interface AlertDialogProps {
  showDialog: boolean;
  setShowDialog?: (show: boolean) => void;
  title: string;
  headerLogo: ReactElement;
  description?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  handleConfirm: () => void;
  handleCancel?: () => void;
  confirmButtonDisabled?: boolean;
  showCancelButton?: boolean;
  showConfirmButton?: boolean;
  type?: "error" | "warning";
  isLoading?: boolean;
}

export default function AlertDialog(
  props: React.PropsWithChildren<AlertDialogProps>,
) {
  const {
    showDialog,
    setShowDialog,
    handleConfirm,
    handleCancel,
    title,
    headerLogo,
    description,
    cancelButtonText,
    confirmButtonText,
    confirmButtonDisabled,
    children,
    showConfirmButton,
    type,
    isLoading,
    showCancelButton,
  } = props;

  const { t } = useTranslation();

  const onClose = () => {
    if (setShowDialog) setShowDialog(false);
  };

  //#region render functions
  const renderHeader = () => {
    return (
      <div className="alert-dialog-container-head">
        <div className="alert-dialog-container-head-alert-header">
          <div className="alert-dialog-container-head-alert-header-top-half">
            <div className="alert-dialog-container-head-alert-header-top-half-left"></div>
            <div className="alert-dialog-container-head-alert-header-top-half-mid">
              <div className="alert-dialog-container-head-alert-header-top-half-mid-ellipse">
                {headerLogo}
              </div>
            </div>
            <div className="alert-dialog-container-head-alert-header-top-half-right"></div>
          </div>
          <div className="alert-dialog-container-head-alert-header-bottom-half">
            <div className="alert-dialog-container-head-alert-header-bottom-half-text">
              <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderActionButtons = () => {
    return (
      <div className="alert-dialog-container-body-action-buttons">
        {(showConfirmButton === undefined || showConfirmButton === true) && (
          <div className="alert-dialog-container-body-action-buttons-confirm">
            <Button
              disabled={confirmButtonDisabled}
              data-testid="confirmButton"
              onClick={handleConfirm}
              autoFocus
              variant="contained"
            >
              {confirmButtonText || t("confirm")}
              {isLoading && <CircularProgress className="ml-10" size={16} />}
            </Button>
          </div>
        )}
        {(showCancelButton === undefined || showCancelButton === true) && (
          <div className="alert-dialog-container-body-action-buttons-cancel">
            <Button
              data-testid="cancelButton"
              onClick={handleCancel || onClose}
              variant="text"
            >
              {cancelButtonText || t("cancel")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderDescription = () => {
    return (
      description && (
        <div className="alert-dialog-container-body-description">
          {description}
        </div>
      )
    );
  };

  const renderBody = () => {
    return (
      <div className="alert-dialog-container-body">
        {renderDescription()}
        {children}
        {renderActionButtons()}
      </div>
    );
  };

  return (
    <div>
      <Dialog
        className={"alert alert-overlay " + type}
        open={showDialog}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        data-testid="alert-dialog"
      >
        <div className="alert-additional-space"></div>
        <div className="alert-dialog-container">
          {renderHeader()}
          {renderBody()}
        </div>
      </Dialog>
    </div>
  );
}
//#endregion
